import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { useAuth } from "@/context/AuthContext";
import api, { money } from "@/lib/api";
import Mascot from "@/components/Mascot";
import {
  TrendingUp, CalendarDays, TrendingDown, PiggyBank, Wrench, Activity, Radar,
} from "lucide-react";

export default function Dashboard() {
  const { user } = useAuth();
  const [kpis, setKpis] = useState(null);
  const [timeline, setTimeline] = useState([]);
  const [radar, setRadar] = useState([]);

  useEffect(() => {
    const load = async () => {
      const [k, t, r] = await Promise.all([
        api.get("/dashboard/kpis"),
        api.get("/dashboard/timeline?limit=12"),
        api.get("/dashboard/demand-radar"),
      ]);
      setKpis(k.data); setTimeline(t.data); setRadar(r.data);
    };
    load();
  }, [user?.active_store_id]);

  const cards = kpis ? [
    { label: "Faturamento do mês", value: money(kpis.revenue_month), icon: TrendingUp, accent: "#10B981" },
    { label: "Faturamento do dia", value: money(kpis.revenue_today), icon: CalendarDays, accent: "#22C55E" },
    { label: "Gastos do mês", value: money(kpis.expenses_month), icon: TrendingDown, accent: "#EF4444" },
    { label: "Gastos do dia", value: money(kpis.expenses_today), icon: TrendingDown, accent: "#F97316" },
    { label: "Reserva fiscal do mês", value: money(kpis.reserve_month), icon: PiggyBank, accent: "#A855F7" },
    { label: "Reserva fiscal do dia", value: money(kpis.reserve_today), icon: PiggyBank, accent: "#8B5CF6" },
  ] : [];

  const maxRadar = Math.max(1, ...radar.map((r) => r.count));

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-3xl sm:text-4xl font-display font-extrabold tracking-tight text-white">
            Olá, {user?.name?.split(" ")[0]} 👋
          </h1>
          <p className="text-zinc-400 mt-1">Seu centro de comando Alpha Prime em tempo real.</p>
        </div>
        <div className="hidden md:block"><Mascot size={84} /></div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
        {cards.map((c, i) => (
          <motion.div key={c.label} data-testid={`kpi-${i}`}
            initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
            className="alpha-card p-5">
            <div className="flex items-center justify-between mb-3">
              <span className="h-9 w-9 rounded-lg flex items-center justify-center" style={{ background: `${c.accent}22` }}>
                <c.icon size={18} style={{ color: c.accent }} />
              </span>
            </div>
            <div className="text-2xl lg:text-3xl font-mono-kpi font-extrabold text-white">{c.value}</div>
            <div className="text-xs text-zinc-500 mt-1">{c.label}</div>
          </motion.div>
        ))}
      </div>

      {kpis && (
        <div className="alpha-card p-6" data-testid="os-status-card">
          <div className="flex items-center gap-2 mb-5">
            <Wrench size={18} className="text-gold" />
            <h3 className="font-display font-semibold text-lg text-white">Ordens de Serviço</h3>
            <span className="text-xs text-zinc-500 ml-auto">Total <span className="text-white font-mono-kpi">{kpis.total_os}</span> · Abertas <span className="text-gold-bright font-mono-kpi">{kpis.open_os}</span></span>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            {(kpis.os_by_status || []).map((s) => (
              <div key={s.key} data-testid={`os-status-${s.key}`} className="rounded-xl border border-white/10 bg-[#0E0E12] p-4">
                <div className="flex items-center gap-2">
                  <span className="h-2.5 w-2.5 rounded-full shrink-0" style={{ background: s.color }} />
                  <span className="text-2xl font-mono-kpi font-extrabold text-white">{s.count}</span>
                </div>
                <div className="text-xs text-zinc-500 mt-1.5 leading-tight">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="alpha-card p-6 lg:col-span-2">
          <div className="flex items-center gap-2 mb-5">
            <Activity size={18} className="text-gold" />
            <h3 className="font-display font-semibold text-lg text-white">Alpha Timeline</h3>
            <span className="text-xs text-zinc-600 ml-auto">Event Stream · retenção permanente</span>
          </div>
          <div className="space-y-3" data-testid="timeline-list">
            {timeline.length === 0 && (
              <div className="text-center py-10 text-zinc-500">
                <Mascot size={72} className="mx-auto mb-3 opacity-80" />
                Nenhum evento ainda. Crie sua primeira OS pelo botão Alpha.
              </div>
            )}
            {timeline.map((ev) => (
              <div key={ev.eventId} className="flex items-start gap-3 border-b border-white/5 pb-3 last:border-0">
                <span className="mt-1.5 h-2 w-2 rounded-full bg-gold shrink-0" />
                <div className="flex-1">
                  <div className="text-sm text-white">{eventLabel(ev)}</div>
                  <div className="text-xs text-zinc-500">{new Date(ev.timestamp).toLocaleString("pt-BR")}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="alpha-card p-6">
          <div className="flex items-center gap-2 mb-5">
            <Radar size={18} className="text-gold" />
            <h3 className="font-display font-semibold text-lg text-white">Radar de Demanda</h3>
          </div>
          <div className="space-y-4" data-testid="demand-radar">
            {radar.length === 0 && <p className="text-sm text-zinc-500">Sem dados de aparelhos ainda.</p>}
            {radar.map((r) => (
              <div key={r.model}>
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-zinc-300 truncate">{r.model}</span>
                  <span className="text-gold-bright font-mono-kpi">{r.count}</span>
                </div>
                <div className="h-2 rounded-full bg-white/5 overflow-hidden">
                  <div className="h-full rounded-full" style={{ width: `${(r.count / maxRadar) * 100}%`, background: "linear-gradient(90deg,#D4AF37,#F3C64F)" }} />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function eventLabel(ev) {
  const num = ev.metadata?.number || "";
  switch (ev.type) {
    case "service_order.created": return `OS ${num} criada`;
    case "service_order.updated": return `OS ${num} atualizada`;
    case "service_order.status_changed": return `OS ${num}: ${ev.metadata?.from} → ${ev.metadata?.to}`;
    default: return ev.type;
  }
}
